import { useEffect, useRef, useState, type ReactNode } from "react";
import { AUTH, BRAND } from "../design/copy.js";
import { BloomBackground } from "../ui/background.js";
import { NotYet, Rule } from "../ui/primitives.js";
import { accountRefusalWords, focusDropped, useAccountSend, type AccountSaid } from "../data/account-send.js";
import type { AuthMode } from "../routes.js";

/**
 * What an email sign-in or sign-up came to, as the app reports it:
 * `signed-in` (the app has moved on), `verify` (no session: the address must
 * be confirmed first, after a sign-up or on better-auth's 403
 * EMAIL_NOT_VERIFIED), or `refused` (the app's `error` says why).
 */
export type EmailSignInResult = "signed-in" | "verify" | "refused";

/**
 * The card's two account-email doors (2026-10-22), handed in by the app; each
 * resolves when the request was answered and rejects when it was not.
 * better-auth answers both the same whether or not the address has an
 * account, so the card says the same thing either way.
 */
export interface AuthAccountDoors {
  readonly resendVerification: (email: string) => Promise<unknown>;
  readonly requestPasswordReset: (email: string) => Promise<unknown>;
}

/** The address waiting to be confirmed, and why the card is waiting. */
export interface AuthWaiting {
  readonly email: string;
  readonly reason: "signed-up" | "unverified";
}

/**
 * What the card holds that a visit to the terms or the privacy page would
 * otherwise lose (review, 2026-10-22): what was typed, and the
 * check-your-email state. The app keeps it in memory, only while the learner
 * is on the card or one of those two pages, and hands it back on return.
 */
export interface AuthDraft {
  readonly name: string;
  readonly email: string;
  readonly password: string;
  readonly waiting: AuthWaiting | null;
}

/**
 * Frame 4. The card sits on the full-bleed loop, 32.4% wide from the `sm`
 * breakpoint and the whole width below it (2026-10-22, as the privacy card
 * was made to fit a phone), `--pc-paper`. The privacy promise is rendered
 * verbatim — it is a commitment, not copy — and the terms notice under it
 * (2026-10-22, Andre) sits above every door: guest, Google and email alike.
 *
 * All three ways in are real now: Google (better-auth's OAuth), email and
 * password, and the guest session. Guest stays visually and functionally
 * first-class — it is still the fastest path to playing, and nothing about
 * signing up later loses the work done before it.
 *
 * HARD VERIFICATION (ruling 8, 2026-10-22). An email sign-up makes no
 * session: the card turns to "Confirm your email address.", says where the
 * link went, how it works, and (when the server says this browser is a
 * guest, `guestWork`) that the guest work moves only if the link is opened
 * here; a sign-in better-auth
 * refuses for an unconfirmed address turns to the same state. Each offers
 * "send another link.". "password reset" opens a form that asks only for
 * the address and says the same thing whether or not it has an account.
 * Focus moves to each new state; what a request came to is said in one live
 * region, present from the first render. A door that goes to rest once its
 * link has left takes its button away, so focus moves to that sentence (the
 * pre-release review, 2026-10-22); a door refused comes back from disabled
 * with focus dropped to the page, so focus moves to the address field when
 * the address is incomplete, else to that sentence (round two). Focus that
 * never dropped is left where it is.
 *
 * The door to send another link opens resting (review, 2026-10-22): the
 * sign-up, or the refused sign-in, has just sent one, and the api holds back
 * a second inside the minute without saying so.
 *
 * The terms and privacy notices sit above every door, so the card reports
 * each change of tab (`onModeChange`: the app keeps it in the address bar,
 * so "back." from either page returns to it) and what it holds
 * (`onDraft`), and opens from a `draft` handed back.
 *
 * Facebook remains inert until credentials exist for it; it is marked as such
 * rather than being offered and failing at the callback.
 */
export function Auth({
  onGuest,
  onFreshGuest,
  guestKnown = false,
  guestWork = false,
  onGoogle,
  onEmail,
  account,
  busy,
  error,
  facebookAvailable = false,
  onFacebook,
  initialMode = "sign-in",
  onModeChange,
  draft,
  onDraft,
}: {
  onGuest: () => void;
  /** Mint a new guest even though this browser already holds one. */
  onFreshGuest?: () => void;
  /** True when this browser already holds a guest identity: the button continues as that guest. */
  guestKnown?: boolean;
  /**
   * True when the server says this browser is a guest (the pre-release review, 2026-10-22), whose
   * work a confirmation opened here brings along: the check-your-email card says so. Not
   * `guestKnown`, a stored cache key that outlives an expired guest and holds an account's own id.
   */
  guestWork?: boolean;
  onGoogle: () => void;
  onEmail: (input: { email: string; password: string; name: string; mode: "sign-in" | "sign-up" }) => Promise<EmailSignInResult>;
  account: AuthAccountDoors;
  busy: boolean;
  error: string | null;
  facebookAvailable?: boolean;
  onFacebook?: () => void;
  /** The tab it opens on: a guest's "create an account." lands on signing up (`#/auth?mode=sign-up`), an expired reset link on the forgot form (`?mode=forgot`). */
  initialMode?: AuthMode;
  /** Each change of tab the learner makes on the card. */
  onModeChange?: (mode: AuthMode) => void;
  /** What the card held when the learner left it for the terms or the privacy page. */
  draft?: AuthDraft | undefined;
  /** Each change to what the card holds. */
  onDraft?: (draft: AuthDraft) => void;
}): ReactNode {
  const [email, setEmail] = useState(draft?.email ?? "");
  const [password, setPassword] = useState(draft?.password ?? "");
  const [name, setName] = useState(draft?.name ?? "");
  const [mode, setModeState] = useState<AuthMode>(initialMode);
  /** The address waiting to be confirmed, and why the card is waiting: null while the doors show. */
  const [waiting, setWaiting] = useState<AuthWaiting | null>(draft?.waiting ?? null);
  const submitting = useRef(false);
  // Handed back the check-your-email state, the door opens resting: its link may have left a moment ago.
  const resend = useAccountSend({ restingOnMount: (draft?.waiting ?? null) !== null });
  const forgot = useAccountSend();

  const setMode = (next: AuthMode): void => {
    setModeState(next);
    onModeChange?.(next);
  };

  const reportDraft = useRef(onDraft);
  reportDraft.current = onDraft;
  useEffect(() => {
    reportDraft.current?.({ name, email, password, waiting });
  }, [name, email, password, waiting]);

  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const forgotFieldRef = useRef<HTMLInputElement | null>(null);
  /** Set by a press that opens the forgot form, so focus follows the press and never a cold arrival. */
  const focusForgot = useRef(false);
  /** Set when a sign-up or sign-in turns the card to waiting, so focus follows that and never a return. */
  const focusTitle = useRef(false);
  useEffect(() => {
    if (waiting === null || !focusTitle.current) return;
    focusTitle.current = false;
    titleRef.current?.focus();
  }, [waiting]);
  useEffect(() => {
    if (mode !== "forgot" || !focusForgot.current) return;
    focusForgot.current = false;
    forgotFieldRef.current?.focus();
  }, [mode]);
  const saidRef = useRef<HTMLParagraphElement | null>(null);
  /** Set by a press of "send another link." or "send a reset link.", so focus follows that door's answer and never a rest begun elsewhere. */
  const focusSaid = useRef<"resend" | "forgot" | null>(null);
  useEffect(() => {
    const pressed = focusSaid.current;
    const door = pressed === "resend" ? { phase: resend.phase, said: resend.said } : pressed === "forgot" ? { phase: forgot.phase, said: forgot.said } : null;
    if (door === null || door.phase === "sending") return;
    focusSaid.current = null;
    // Focus that never dropped (Enter in the address field) stays where it is.
    if (!focusDropped()) return;
    // Resting, the pressed button is gone from under the keyboard; refused (the pre-release review, round
    // two, 2026-10-22), it was disabled while it sent and the browser let go of it. An incomplete address
    // is corrected in the reset form's field; anything else, and the waiting card's refusals (it has no
    // field), is read where it is said.
    if (door.phase === "ready" && door.said === "addressIncomplete" && pressed === "forgot") forgotFieldRef.current?.focus();
    else saidRef.current?.focus();
  }, [resend.phase, resend.said, forgot.phase, forgot.said]);

  const signingUp = mode === "sign-up";
  // Mirrors the server's minimum (better-auth `minPasswordLength`), so the
  // learner is told before the round trip rather than by a rejection.
  const passwordTooShort = signingUp && password.length > 0 && password.length < 12;
  const canSubmit = email.trim().length > 0 && password.length > 0 && !passwordTooShort && (!signingUp || name.trim().length > 0);

  const submit = async (): Promise<void> => {
    if (!canSubmit || busy || submitting.current || mode === "forgot") return;
    submitting.current = true;
    const address = email.trim();
    try {
      const result = await onEmail({ email: address, password, name: name.trim(), mode });
      if (result === "verify") {
        // The sign-up, or better-auth on the refused sign-in, has just sent a link: the door rests.
        resend.rest();
        focusTitle.current = true;
        setWaiting({ email: address, reason: signingUp ? "signed-up" : "unverified" });
      }
    } finally {
      submitting.current = false;
    }
  };

  const backToSignIn = (): void => {
    focusSaid.current = null;
    resend.reset();
    forgot.reset();
    setWaiting(null);
    setMode("sign-in");
  };

  const openForgot = (): void => {
    focusSaid.current = null;
    forgot.reset();
    focusForgot.current = true;
    setMode("forgot");
  };

  const field =
    "w-full border border-ink bg-paper px-4 py-2 font-sans text-body text-ink outline-none placeholder:font-display placeholder:font-bold placeholder:text-ink-faint";
  const action =
    "w-fit cursor-pointer border-0 bg-transparent p-0 text-left font-sans text-nav text-ink hover:bg-marker disabled:cursor-wait disabled:text-ink-faint";
  const quiet = "w-fit cursor-pointer border-0 bg-transparent p-0 font-sans text-nav text-ink-soft underline underline-offset-2 hover:bg-marker";

  const saidWords = (said: AccountSaid | null, sent: string): string | null => (said === null ? null : said === "sent" ? sent : accountRefusalWords(said));
  const liveLine = waiting !== null ? saidWords(resend.said, AUTH.resent) : mode === "forgot" ? saidWords(forgot.said, AUTH.forgotSent) : error;

  return (
    <div className="relative flex min-h-svh items-center justify-center">
      <BloomBackground />
      <div data-testid="auth-card" className="relative z-10 w-full bg-paper px-6 py-10 sm:w-[32.4%] sm:min-w-[34rem] sm:px-16 sm:py-12">
        <div className="font-sans text-nav">{AUTH.header}</div>
        <Rule className="mt-2" />
        <h1 className="mt-6 mb-0 text-right font-display text-display font-bold">
          {BRAND.wordmarkLine1}
          <br />
          {BRAND.wordmarkLine2}
        </h1>

        <p className="mt-10 mb-0 font-sans text-body">
          {AUTH.privacyBeforeLink}
          <a href="#/privacy" className="text-link underline">
            {AUTH.privacyLink}
          </a>
          {AUTH.privacyAfterLink}
        </p>
        {/* The terms notice (2026-10-22): above every door, so continuing by any of them is agreeing. */}
        <p className="mt-2 mb-0 font-sans text-small">
          {AUTH.termsBeforeLink}
          <a href="#/terms" className="text-link underline">
            {AUTH.termsLink}
          </a>
          {AUTH.termsAfterLink}
        </p>
        <Rule className="mt-4" />

        {waiting !== null ? (
          <section className="mt-10">
            <h2 ref={titleRef} tabIndex={-1} className="m-0 font-display text-nav font-bold outline-none">
              {AUTH.checkEmailTitle}
            </h2>
            <p className="mt-4 mb-0 font-sans text-body">{waiting.reason === "signed-up" ? AUTH.checkEmailSignedUp(waiting.email) : AUTH.checkEmailUnverified(waiting.email)}</p>
            <p className="mt-4 mb-0 font-sans text-body">{AUTH.checkEmailOpen}</p>
            {guestWork ? <p className="mt-4 mb-0 font-sans text-body">{AUTH.checkEmailGuest}</p> : null}
            <p className="mt-4 mb-0 font-sans text-small text-ink-soft">{waiting.reason === "signed-up" ? AUTH.checkEmailLastsSignedUp : AUTH.checkEmailLasts}</p>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              {resend.phase === "resting" ? null : (
                <button
                  type="button"
                  disabled={resend.phase === "sending"}
                  onClick={() => {
                    focusSaid.current = "resend";
                    void resend.send(() => account.resendVerification(waiting.email));
                  }}
                  className={action}
                >
                  {AUTH.resend}
                </button>
              )}
              <button type="button" onClick={backToSignIn} className={quiet}>
                {AUTH.backToSignIn}
              </button>
            </div>
          </section>
        ) : mode === "forgot" ? (
          <form
            className="mt-10 flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              const address = email.trim();
              if (address.length === 0) return;
              focusSaid.current = "forgot";
              void forgot.send(() => account.requestPasswordReset(address));
            }}
          >
            <p className="m-0 font-sans text-body">{AUTH.forgotIntro}</p>
            <input
              ref={forgotFieldRef}
              aria-label={AUTH.emailPlaceholder}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={AUTH.emailPlaceholder}
              className={field}
            />
            <div className="flex flex-wrap items-center gap-6">
              {forgot.phase === "resting" ? null : (
                <button type="submit" disabled={forgot.phase === "sending" || email.trim().length === 0} className={action}>
                  {AUTH.forgotSend}
                </button>
              )}
              <button type="button" onClick={backToSignIn} className={quiet}>
                {AUTH.backToSignIn}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="mt-10 flex flex-col gap-1">
              <button type="button" onClick={onGoogle} disabled={busy} className={action}>
                {AUTH.google}
              </button>
              {facebookAvailable && onFacebook !== undefined ? (
                <button type="button" onClick={onFacebook} disabled={busy} className={action}>
                  {AUTH.facebook}
                </button>
              ) : (
                <NotYet>
                  <span className="font-sans text-nav">{AUTH.facebook}</span>
                </NotYet>
              )}
              <button type="button" onClick={onGuest} disabled={busy} className={action}>
                {busy ? "Guest Session. starting…" : guestKnown ? AUTH.guestContinue : AUTH.guest}
              </button>
              {guestKnown && onFreshGuest !== undefined ? (
                <button type="button" onClick={onFreshGuest} disabled={busy} className="self-start font-sans text-micro text-ink-soft underline-offset-2 hover:underline">
                  {AUTH.guestFresh}
                </button>
              ) : null}
              <div className="font-sans text-nav text-ink-soft">{AUTH.separator}</div>
              <div className="font-sans text-nav">{AUTH.emailIntro}</div>
            </div>

            <form
              className="mt-6 flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              {signingUp ? (
                <input aria-label={AUTH.namePlaceholder} value={name} onChange={(e) => setName(e.target.value)} placeholder={AUTH.namePlaceholder} className={field} />
              ) : null}
              <input
                aria-label={AUTH.emailPlaceholder}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={AUTH.emailPlaceholder}
                className={field}
              />
              <input
                aria-label={AUTH.passwordPlaceholder}
                type="password"
                autoComplete={signingUp ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={AUTH.passwordPlaceholder}
                className={field}
              />
              {passwordTooShort ? <p className="m-0 font-sans text-small text-ink-soft">{AUTH.passwordTooShort}</p> : null}

              <div className="flex flex-wrap items-center gap-6">
                <button type="submit" disabled={busy || !canSubmit} className={action}>
                  {signingUp ? AUTH.signUp : AUTH.signIn}
                </button>
                <button type="button" onClick={() => setMode(signingUp ? "sign-in" : "sign-up")} className={quiet}>
                  {signingUp ? AUTH.toggleToSignIn : AUTH.toggleToSignUp}
                </button>
              </div>
              {/* The frames' "password reset" (lowercase, no period, underlined, --pc-link), live since 2026-10-22. */}
              <button type="button" onClick={openForgot} className="ml-4 w-fit cursor-pointer border-0 bg-transparent p-0 text-left font-sans text-body text-link underline hover:bg-marker">
                {AUTH.passwordReset}
              </button>
            </form>
          </>
        )}

        {/* One live region, present from the first render: a sentence inserted into it is announced. */}
        <div aria-live="polite">
          {liveLine !== null ? (
            <p ref={saidRef} tabIndex={-1} className="mt-6 mb-0 font-sans text-body text-ink-soft outline-none">
              {liveLine}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
